import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api, { apiErr } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import { ShieldCheck, Lock, Tag, CheckCircle2, Smartphone, CreditCard, Landmark, Wallet } from "lucide-react";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const [params] = useSearchParams();
  const type = params.get("type");
  const slug = params.get("slug");
  const { user, location } = useApp();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!location || !slug) return;
    api.logEvent("checkout.started", { type, slug });
    const fetcher = type === "bundle" ? api.bundle(slug, location.country) : api.course(slug, location.country);
    fetcher.then((d) => {
      setItem(type === "bundle"
        ? { title: d.title, price: d.offer_price, symbol: d.symbol, currency: d.currency, count: d.course_slugs.length, image: d.image }
        : { title: d.title, price: d.price, symbol: d.symbol, currency: d.currency, count: 1, image: d.image });
    }).catch(() => setItem(false));
  }, [type, slug, location]);

  if (item === false) return <div className="max-w-2xl mx-auto px-6 py-32 text-center text-slate-500">Item not found.</div>;
  if (!item) return <div className="max-w-2xl mx-auto px-6 py-32 text-center text-slate-500">Loading…</div>;

  const sym = item.symbol;
  const isINR = item.currency === "INR";

  const pay = async () => {
    setPaying(true);
    try {
      const order = await api.checkoutCreate({ item_type: type, slug, coupon: coupon || null });
      if (order.mock) {
        // MOCKED payment (no gateway keys configured yet)
        await api.checkoutConfirm({ order_id: order.order_id });
        toast.success("Payment successful!");
        navigate("/payment/success", { state: { unlock: true } });
        return;
      }
      // Real Razorpay
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment gateway."); setPaying(false); return; }
      const rzp = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: order.amount * 100,
        currency: "INR",
        name: "AI Scale Force Store",
        description: order.title,
        order_id: order.razorpay_order_id,
        prefill: { name: user.name, email: user.email, contact: user.phone },
        theme: { color: "#FF5A00" },
        handler: async (resp) => {
          try {
            await api.checkoutConfirm({
              order_id: order.order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful!");
            navigate("/payment/success", { state: { unlock: true } });
          } catch (e) { toast.error(apiErr(e)); navigate("/payment/failed"); }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.open();
    } catch (err) {
      toast.error(apiErr(err));
      setPaying(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-16 grid lg:grid-cols-5 gap-8" data-testid="checkout-page">
      <div className="lg:col-span-3">
        <div className="asf-overline mb-3">Secure checkout</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Complete your purchase</h1>

        <div className="asf-card !translate-y-0 hover:!translate-y-0 p-6 mt-6">
          <h3 className="font-display text-lg font-semibold mb-4">Billing details</h3>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div><div className="text-slate-500 text-xs mb-1">Name</div><div>{user.name}</div></div>
            <div><div className="text-slate-500 text-xs mb-1">Email</div><div className="truncate">{user.email}</div></div>
            <div><div className="text-slate-500 text-xs mb-1">Phone</div><div>{user.phone || "—"}</div></div>
            <div><div className="text-slate-500 text-xs mb-1">Country</div><div>{location?.country_name}</div></div>
          </div>
        </div>

        <div className="asf-card !translate-y-0 hover:!translate-y-0 p-6 mt-4">
          <label className="text-sm text-slate-600 flex items-center gap-2 mb-2"><Tag className="h-4 w-4 text-asf-orange" /> Coupon code</label>
          <input data-testid="coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())}
            placeholder="Enter code (optional)"
            className="w-full rounded-xl bg-asf-app border border-slate-200 px-4 py-3 outline-none focus:border-asf-orange font-mono uppercase" />
        </div>

        <div className="mt-4 rounded-xl bg-asf-orange/10 border border-asf-orange/25 p-4 text-sm text-slate-700 flex items-center gap-2" data-testid="currency-notice">
          <ShieldCheck className="h-4 w-4 text-asf-orange" />
          You are paying in <strong>{item.currency}</strong> via {isINR ? "Razorpay" : "Stripe"}.
        </div>

        {isINR && (
          <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4" data-testid="payment-methods-inr">
            <div className="text-xs font-semibold text-slate-500 mb-3">Pay your way — all methods supported</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[[Smartphone, "UPI", "GPay, PhonePe, Paytm"], [CreditCard, "Cards", "Credit & Debit"], [Landmark, "NetBanking", "All major banks"], [Wallet, "EMI", "No-cost options"]].map(([Icon, label, sub]) => (
                <div key={label} className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-center">
                  <Icon className="h-5 w-5 text-asf-orange mx-auto" />
                  <div className="text-sm font-semibold text-slate-800 mt-1.5">{label}</div>
                  <div className="text-[10px] text-slate-500 leading-tight mt-0.5">{sub}</div>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-slate-500 mt-3 flex items-center gap-1.5">
              <Smartphone className="h-3 w-3 text-asf-orange" /> Split into easy EMIs at checkout — pay from ₹{Math.round(item.price / 6).toLocaleString()}/mo
            </div>
          </div>
        )}
      </div>

      {/* summary */}
      <div className="lg:col-span-2">
        <div className="asf-card !translate-y-0 hover:!translate-y-0 p-6 lg:sticky lg:top-24">
          <h3 className="font-display text-lg font-semibold mb-4">Order summary</h3>
          <div className="flex gap-3">
            <img src={item.image} alt="" className="h-16 w-16 rounded-lg object-cover" />
            <div>
              <div className="font-medium leading-tight">{item.title}</div>
              <div className="text-xs text-slate-500 mt-1">{item.count} course{item.count > 1 ? "s" : ""} · lifetime access</div>
            </div>
          </div>
          <div className="border-t border-slate-200 mt-5 pt-4 flex items-center justify-between">
            <span className="text-slate-500">Total</span>
            <span className="font-display text-2xl font-extrabold">{fmtMoney(sym, item.price)}</span>
          </div>
          <button data-testid="pay-now-btn" onClick={pay} disabled={paying} className="btn-primary w-full py-4 mt-5 inline-flex items-center justify-center gap-2">
            <Lock className="h-4 w-4" /> {paying ? "Processing…" : `Pay ${fmtMoney(sym, item.price)}`}
          </button>
          <p className="text-[11px] text-slate-500 mt-3 text-center leading-relaxed" data-testid="checkout-policy-links">
            By completing this payment you agree to our{" "}
            <a href="/terms" target="_blank" rel="noreferrer" className="text-asf-orange hover:underline">Terms &amp; Conditions</a>,{" "}
            <a href="/privacy" target="_blank" rel="noreferrer" className="text-asf-orange hover:underline">Privacy Policy</a>{" "}and{" "}
            <a href="/refund" target="_blank" rel="noreferrer" className="text-asf-orange hover:underline">Refund Policy</a>.
          </p>
          <ul className="mt-5 space-y-2 text-xs text-slate-500">
            {["Instant access after payment", "30-day satisfaction policy", "Secure & encrypted"].map((f) => (
              <li key={f} className="flex gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-asf-orange" /> {f}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
